import { PAGE_TYPES } from '../enums/page-types';
import { getMinimumChargeAmount, isBelowMinimumChargeAmount } from '../services/currency';
import { formatPrice } from '../services/formatPrice';
import checkIsVariantEnabled from '../lib/checkIsVariantEnabled';
import checkIsVariantVisible from '../lib/checkIsVariantVisible';
import { htmlToPlainText } from '../lib/htmlToPlainText';
import hasStock from '../lib/hasStock';
import useCartCost from './useCartCost';
import useCheckoutState from './useCheckoutState';

const useCheckoutValidation = () => {
  const {
    checkout,
    product,
    form,
    isUpsell,
    isTesting,
    paymentGateway,
    paymentMethod,
    query,
    paymentOption,
  } = useCheckoutState();
  const { totalDueAmount } = useCartCost();

  const shouldValidateCardDetails =
    !isUpsell &&
    isTesting &&
    totalDueAmount > 0 &&
    paymentOption.type !== 'manual' &&
    paymentGateway === 'stripe' &&
    paymentMethod === 'card';

  function validateMinimumChargeAmount(amount, currency, locale) {
    if (isBelowMinimumChargeAmount(amount, currency)) {
      throw new Error(
        `The minimum transaction amount in ${currency.toUpperCase()} is ${formatPrice({
          amount: getMinimumChargeAmount(currency),
          currency,
          locale,
        })}`,
      );
    }
  }

  function validateHasStock() {
    if (checkout.type !== PAGE_TYPES.CHECKOUT) return;

    if (!hasStock(product)) {
      throw new Error('This item is sold out.');
    }
  }

  function validateVariants() {
    if (product.variants && product.variants.length > 0) {
      // validate if all required variants have a value in form.variants and are visible
      const requiredVariants = product.variants.filter(variant => {
        const enabled = checkIsVariantEnabled(variant, form.variants, product.variants);

        const visible = checkIsVariantVisible(variant, query);

        return variant.required && enabled && visible;
      });

      const requiredVariantsWithoutQuantity = requiredVariants.filter(variant => {
        const selectedOptions = form.variants[variant._id];

        if (!selectedOptions || !selectedOptions.length) return true;

        const hasSelectedOptionWithQuantity = selectedOptions.find(option => option.quantity > 0);

        return !hasSelectedOptionWithQuantity;
      });

      if (requiredVariantsWithoutQuantity.length > 0) {
        throw new Error(
          `${htmlToPlainText(requiredVariantsWithoutQuantity[0].name)} ${checkout.variantIsRequiredText}`,
        );
      }
    }
  }

  return {
    shouldValidateCardDetails,
    validateMinimumChargeAmount,
    validateHasStock,
    validateVariants,
  };
};

export default useCheckoutValidation;
